import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { Faq } from "../components/Faq";
import { CtaBand } from "../components/CtaBand";
import { Seo, faqJsonLd, breadcrumbJsonLd, serviceJsonLd } from "../components/Seo";
import { T, paths } from "../i18n";
import { getService, SERVICES } from "../data/services";
import { getCity } from "../data/cities";
import { comboContent } from "../content";
import { isIndexableCombo } from "../seoPolicy";

export default function ComboPage({ lang }) {
  const { serviceSlug, citySlug } = useParams();
  const t = T[lang];
  const p = paths(lang);
  const s = getService(serviceSlug);
  const c = getCity(citySlug);
  const content = comboContent(serviceSlug, citySlug, lang);
  if (!s || !c || !content) return <Navigate to={p.services} replace />;

  const Icon = s.icon;
  const cityName = lang === "fr" ? c.fr : c.en;
  const indexable = isIndexableCombo(serviceSlug, citySlug);
  const otherServices = SERVICES.filter((x) => x.slug !== serviceSlug && isIndexableCombo(x.slug, citySlug));
  const nearbyIndexable = content.nearby.filter((n) => isIndexableCombo(serviceSlug, n.slug));

  return (
    <div data-testid={`combo-page-${serviceSlug}-${citySlug}`}>
      <Seo lang={lang} title={content.title} description={content.description} noindex={!indexable}
        enPath={`/security-services/${serviceSlug}/${citySlug}`} frPath={`/fr/services-de-securite/${serviceSlug}/${citySlug}`}
        jsonLd={[
          serviceJsonLd(content.h1, content.description, (lang === "fr" ? `/fr/services-de-securite/${serviceSlug}/${citySlug}` : `/security-services/${serviceSlug}/${citySlug}`), `${cityName}, Quebec, Canada`),
          faqJsonLd(content.faq),
          breadcrumbJsonLd([[t.breadcrumbHome, p.home], [t.breadcrumbServices, p.services], [s[lang].name, p.service(serviceSlug)], [cityName, null]])
        ]} />
      <PageHeader lang={lang} eyebrow={`${s[lang].name} · ${cityName}`} title={content.h1}
        breadcrumbs={[[t.breadcrumbHome, p.home], [t.breadcrumbServices, p.services], [s[lang].name, p.service(serviceSlug)], [cityName, null]]} />

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Reveal>
              <div className="mb-6 inline-flex rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4"><Icon size={34} className="text-amber-400" /></div>
              {content.body.map((para, i) => (
                <p key={i} className="mb-5 text-base leading-relaxed text-slate-300">{para}</p>
              ))}
            </Reveal>
            <Reveal>
              <h2 className="mt-10 font-display text-2xl font-bold tracking-tight sm:text-3xl">{t.includedTitle}</h2>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {s[lang].features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 rounded-xl border border-slate-800/70 bg-card/60 px-4 py-3 text-sm text-slate-300">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-amber-400" /> {f}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div className="rounded-2xl border border-amber-500/25 bg-card/80 p-6">
                <h2 className="font-display text-lg font-bold">{t.ctaBandTitle}</h2>
                <a href="tel:+18077700243" className="mt-5 block rounded-full bg-amber-500 px-5 py-3 text-center text-sm font-bold text-obsidian transition-colors hover:bg-amber-300" data-testid="combo-aside-call">+1 807-770-0243</a>
                <a href="mailto:admin@1stnation-security.com" className="mt-3 block rounded-full border border-amber-500/50 px-5 py-3 text-center text-sm font-semibold text-amber-300 transition-colors hover:bg-amber-500/10" data-testid="combo-aside-email">admin@1stnation-security.com</a>
              </div>
              {nearbyIndexable.length > 0 && (
                <div className="rounded-2xl border border-slate-800/70 bg-card/60 p-6">
                  <h2 className="font-display text-base font-bold">
                    {lang === "fr" ? `${s.fr.name} — villes voisines` : `${s.en.name} — nearby cities`}
                  </h2>
                  <ul className="mt-4 space-y-2">
                    {nearbyIndexable.map((n) => (
                      <li key={n.slug}>
                        <Link to={p.combo(serviceSlug, n.slug)} className="text-sm text-slate-400 transition-colors hover:text-amber-300" data-testid={`combo-nearby-${n.slug}`}>
                          {lang === "fr" ? n.fr : n.en}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>

      {otherServices.length > 0 && (
        <section className="border-t border-slate-800/60 bg-card/30 py-14">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{t.alsoIn} {cityName}</h2>
            </Reveal>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {otherServices.map((os) => {
                const OsIcon = os.icon;
                return (
                  <Link key={os.slug} to={p.combo(os.slug, citySlug)} className="group flex items-start gap-3 rounded-xl border border-slate-800/70 bg-obsidian/60 p-5 transition-all hover:-translate-y-0.5 hover:border-amber-500/40" data-testid={`combo-other-${os.slug}`}>
                    <OsIcon size={20} className="mt-0.5 shrink-0 text-amber-400" />
                    <div>
                      <h3 className="text-sm font-bold text-slate-200 group-hover:text-amber-300">{os[lang].name}</h3>
                      <span className="mt-1.5 flex items-center gap-1 text-xs font-semibold text-amber-400">{t.viewService} <ArrowRight size={12} /></span>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to={p.city(citySlug)} className="text-sm font-semibold text-amber-400 hover:text-amber-300" data-testid="combo-parent-city">
                {lang === "fr" ? `← Tous les services à ${c.fr}` : `← All security services in ${c.en}`}
              </Link>
              <Link to={p.service(serviceSlug)} className="text-sm font-semibold text-amber-400 hover:text-amber-300" data-testid="combo-parent-service">
                {lang === "fr" ? `← ${s.fr.name} au Québec` : `← ${s.en.name} across Quebec`}
              </Link>
            </div>
          </div>
        </section>
      )}

      <Faq items={content.faq} title={t.faqTitle} />
      <CtaBand lang={lang} />
    </div>
  );
}
